import type { EmployerSiteName } from '@seek/adv-header-footer';
import type { Language } from '@seek/melways-sites';
import type { useToast } from 'braid-design-system';

import { HOTJAR_OVER_SIXTY_APPLICATIONS } from 'src/constants/hotjar';
import { PER_PAGE } from 'src/constants/numPerPage';
import type { PageConfig } from 'src/libs/PageConfig';
import type { TrackingExperimentsAttributes } from 'src/utils/eventsTypes';
import type { UseBucketLabelResponse } from 'src/utils/hooks/useBucketLabel';
import { sendHotjarTag } from 'src/utils/hotjar';

import type { IJobApplicationStore } from '../../stores/JobApplicationStore';
import type { IJobApplication } from '../../stores/JobApplicationStore/JobApplication';
import type { IStore } from '../../stores/Store';
import type { QueryParams } from '../../utils/hooks/types';

import handleEasyAccess from './handleEasyAccess';
import changeBucketForSelectedJobApplication from './utils/changeBucketForSelectedJobApplication';

import type { HandleEasyAccessTranslations } from '.';

const notInTheRightBucket = (store: IStore) =>
  store.jobApplicationStore.uiState.selectedProspectId &&
  !store.jobApplicationStore.shownDetailsJobApplication;

const notOnRightPage = (store: IStore) => {
  const selectedProspectId =
    store.jobApplicationStore.uiState.selectedProspectId;

  const selectedProspectIdNotFoundInCurrentPage =
    !store.jobApplicationStore.currentPageJobApplications.find(
      (ja: { id: string }) => ja.id === selectedProspectId,
    );

  return selectedProspectId && selectedProspectIdNotFoundInCurrentPage;
};

/**
 * findPageOfApplication
 *
 * @description function to find the page of a given Job Application by prospectId using `positionInList`
 * The `positionInList` of a given Job App is its index in the list of the current search filters (bucket + search text + other filters)
 * It is best to call this function after we have calling loadedAllJobApplications function because it updates the `positionInList` to the right value
 */
const findPageOfApplication = (
  store: IStore,
  prospectId: string,
  addCustomError: PageConfig['dataDogRum']['addCustomError'],
): number | null => {
  const loadedJobApp: IJobApplication =
    store.jobApplicationStore.loadedJobApplications.get(prospectId);

  if (!loadedJobApp) {
    addCustomError({
      error: 'CANT_FIND_PAGE_OF_APP',
      context: {
        description:
          'Unable to find job application when attempting to navigate directly to the candidate',
        prospectId,
      },
    });
    return null;
  }
  return Math.ceil(loadedJobApp.positionInList / PER_PAGE);
};

const pageOutOfRange = (jobApplicationStore: IJobApplicationStore) =>
  jobApplicationStore.uiState.page > jobApplicationStore.numberOfPages ||
  jobApplicationStore.uiState.page < 1;
const fetchInitialJobApplications = async (
  store: IStore,
  setPageQueryString: (page: number | undefined) => void,
  advertiserId: string,
  seekAdvertiserId: string,
  positionId: number,
  jobId: number,
  queryParams: QueryParams,
  showToast: ReturnType<typeof useToast>,
  getLabelFromBucket: UseBucketLabelResponse['getLabelFromBucket'],
  messages: HandleEasyAccessTranslations,
  language: Language,
  site: EmployerSiteName,
  addCustomError: PageConfig['dataDogRum']['addCustomError'],
  experiments?: TrackingExperimentsAttributes[],
) => {
  const { jobApplicationStore } = store;
  await store.initLoad(
    advertiserId,
    seekAdvertiserId,
    positionId,
    jobId,
    queryParams,
    language,
    site,
    addCustomError,
    experiments,
  );

  handleEasyAccess(
    store,
    showToast,
    getLabelFromBucket,
    messages,
    addCustomError,
  );

  if (notInTheRightBucket(store)) {
    await changeBucketForSelectedJobApplication(
      store,
      jobId,
      jobApplicationStore.uiState.selectedProspectId,
    );
  }

  if (notOnRightPage(store)) {
    await jobApplicationStore.loadAllJobApplications({
      selectAll: false,
      shouldShowLoading: true,
      filters: {
        rrGlobalFilters: [],
      },
      addCustomError,
    });
    const correctPage = findPageOfApplication(
      store,
      jobApplicationStore.uiState.selectedProspectId,
      addCustomError,
    );
    if (correctPage) {
      setPageQueryString(correctPage);
    }
  }

  if (pageOutOfRange(store.jobApplicationStore)) {
    setPageQueryString(undefined);
  }

  if (jobApplicationStore.bucketStats?.totalCount ?? 0 > 60) {
    sendHotjarTag(HOTJAR_OVER_SIXTY_APPLICATIONS);
  }
};

export default fetchInitialJobApplications;
